import React, { useState, useEffect, useCallback, useMemo} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
// --- Child Components ---
import InvoiceHeader from "../components/InvoiceHeader";
import InvoiceItemsTable from "../components/InvoiceItemsTable";
import InvoiceTotals from "../components/InvoiceTotals";

// --- Styles ---
import "../Styles/Invoice.css";
// --- Import the shared print utility ---
import { handlePrint } from "../api/printUtils";
import PrintToOfficeModal from "../components/PrintToOfficeModal"; // <-- Import the new component
const API_URL = process.env.REACT_APP_API_URL;

const CreateInvoice = () => {
    // --- Hooks ---
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    // --- State Management (no changes) ---
    const [docType, setDocType] = useState("INVOICE");
    const [docno, setDocno] = useState("");
    const [invoiceDate, setInvoiceDate] = useState(
      new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" })
    );
    const [customers, setCustomers] = useState([]);
    const [customerCode, setCustomerCode] = useState("");
    const [poNumber, setPoNumber] = useState("");
    const [poDate, setPoDate] = useState("");
    const [lineItems, setLineItems] = useState([{ prodcode: "", prodname: "", qty: "", rate: "", taxper: 0, uom: "", hsncode: "", totalvalue: 0, originalTax: 0,suggestions: [], showSuggestions: false  }]);
    const [discounts, setDiscounts] = useState([]);
    const [discountPercent, setDiscountPercent] = useState(0);
    const [selectedDiscountCode, setSelectedDiscountCode] = useState("");
    const [remarks, setRemarks] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [taxApplicable, setTaxApplicable] = useState(true);
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false); // <-- State to control modal
    const [createdDoc, setCreatedDoc] = useState(null);
    // --- Data Fetching & Effects (no changes) ---
    useEffect(() => {
        axios.get(`${API_URL}/customers`, { headers }).then(res => setCustomers(res.data));
        axios.get(`${API_URL}/discounts`, { headers }).then(res => setDiscounts(res.data));
    }, [headers]);
    
    const getFinYear = useCallback((dateStr) => {
        const date = new Date(dateStr);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        return month <= 3 ? `${year - 1}-${year}` : `${year}-${year + 1}`;
    }, []);

    useEffect(() => {
        if (docType && invoiceDate) {
            const finYear = getFinYear(invoiceDate);
            axios.get(`${API_URL}/new-docno?doctype=${docType}&finyear=${finYear}`, { headers })
                .then((res) => setDocno(res.data.docno))
                .catch(err => console.error("Failed to fetch new docno:", err));
        }
    }, [docType, invoiceDate, getFinYear, headers]);

    useEffect(() => {
        setLineItems(prevItems => 
            prevItems.map(item => ({
                ...item,
                taxper: taxApplicable ? item.originalTax || 0 : 0
            }))
        );
    }, [taxApplicable]);
    useEffect(() => {
    // If there are any items in the list, reset their suggestions
    // to prevent showing stale data from the previous customer.
    if (lineItems.length > 0 && lineItems[0].prodcode) {
         setLineItems(prevItems =>
            prevItems.map(item => ({
                ...item,
                suggestions: [],
                showSuggestions: false
            }))
        );
    }
}, [customerCode]);
    // --- Calculations (no changes) ---
    const calculatedData = useMemo(() => {
        const subtotal = lineItems.reduce((sum, item) => sum + (parseFloat(item.totalvalue) || 0), 0);
        const discountAmt = subtotal * (parseFloat(discountPercent) / 100);
        const totalNetValue = subtotal - discountAmt;
        const totalTax = lineItems.reduce((sum, item) => {
            const itemBasicValue = parseFloat(item.totalvalue) || 0;
            const itemDiscountShare = subtotal > 0 ? (itemBasicValue / subtotal) * discountAmt : 0;
            const itemNetValue = itemBasicValue - itemDiscountShare;
            const tax = itemNetValue * (parseFloat(item.taxper) / 100);
            return sum + tax;
        }, 0);
        const grandTotal = totalNetValue + totalTax;
        return { subtotal, discountAmt, totalTax, grandTotal };
    }, [lineItems, discountPercent]);

    // --- MODIFICATION 1: Extracted document creation logic into a reusable function ---
    const createDocument = async () => {
        if (!customerCode) {
            toast.error('Please select a customer.');
            return null; // Return null on validation failure
        }
        if (lineItems.some(item => !item.prodcode)) {
            toast.error(("Please ensure all lines have a product selected."));
            return null;
        }
         // Manual validation for Qty and Rate
        const hasInvalidItem = lineItems.some(item => 
        !item.qty || parseFloat(item.qty) <= 0 || !item.rate || parseFloat(item.rate) <= 0
        );
        if (hasInvalidItem) {
            toast.error(("Please ensure all items have a quantity and rate greater than zero."));
            return null;
        }
        
        const payload = {
            docdate: invoiceDate, customercode: customerCode, pono: poNumber,
            podate: poDate || null, discount_percent: parseFloat(discountPercent) || 0, remarks,
            items: lineItems.map(item => ({
                prodcode: item.prodcode, qty: parseFloat(item.qty) || 0,
                rate: parseFloat(item.rate) || 0, uom: item.uom || "",
                taxper: parseFloat(item.taxper) || 0
            }))
        };
        
        // This will throw an error if it fails, which will be caught by the calling function
        const response = await axios.post(`${API_URL}/document?doctype=${docType}`, payload, { headers });
        return response.data; // Returns { docno, finyear, message }
    };

    // --- MODIFICATION 2: Renamed original handler to be more specific ---
    const handleSubmitAndPrintLocal = async () => {
        setIsSubmitting(true);
        try {
            const savedDoc = await createDocument();
            if (savedDoc) {
                await handlePrint(savedDoc.docno, savedDoc.finyear, docType);
                toast.success(`${docType} created successfully!`);
                navigate("/dashboard");
            }
        } catch (err) {
            let errorDetail = `Failed to create ${docType}.\n` + JSON.stringify(err.response?.data?.detail || "An unknown error occurred.", null, 2);
            toast.error((errorDetail));
            console.error("Error creating document:", err.response ? err.response.data : err);
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- MODIFICATION 3: Added new handler for the office print button ---
    const handleSubmitAndPrintAtOffice = async () => {
  setIsSubmitting(true);
  try {
    const savedDoc = await createDocument();
    if (!savedDoc) {
      setIsSubmitting(false);
      return;
    }

    // Store the doc info and open the modal
    setCreatedDoc(savedDoc);
    setIsPrintModalOpen(true);

  } catch (err) {
    let errorDetail =
      `Failed to create ${docType}.\n` +
      JSON.stringify(
        err.response?.data?.detail || "An unknown error occurred.",
        null,
        2
      );
    toast.error(errorDetail);
    console.error("Error creating document:", err.response ? err.response.data : err);
  } finally {
    setIsSubmitting(false);
  }
};

    // --- Render ---
    return (
        <>
            <div className="invoice-form-container">
                <InvoiceHeader
                    docType={docType} setDocType={setDocType} docno={docno}
                    invoiceDate={invoiceDate} setInvoiceDate={setInvoiceDate}
                    customers={customers} customerCode={customerCode} setCustomerCode={setCustomerCode}
                    poNumber={poNumber} setPoNumber={setPoNumber}
                    poDate={poDate} setPoDate={setPoDate}
                    taxApplicable={taxApplicable}
                    setTaxApplicable={setTaxApplicable}
                />
                <InvoiceItemsTable
                    lineItems={lineItems} setLineItems={setLineItems}
                    headers={headers}
                    taxApplicable={taxApplicable}
                    customerCode={customerCode}
                />
                <InvoiceTotals
                    remarks={remarks}
                    setRemarks={setRemarks}
                    discounts={discounts}
                    selectedDiscountCode={selectedDiscountCode}
                    setSelectedDiscountCode={setSelectedDiscountCode}
                    setDiscountPercent={setDiscountPercent}
                    calculatedData={calculatedData}
                    lineItems={lineItems}
                    discountPercent={discountPercent}
                    taxApplicable={taxApplicable}
                />

                {/* --- MODIFICATION 4: Updated the form actions area --- */}
                <div className="form-actions">
                    <button onClick={handleSubmitAndPrintLocal} disabled={isSubmitting} className="submit-btn">
                        {isSubmitting ? 'Submitting...' : `Create & Print`}
                    </button>
                    <button onClick={handleSubmitAndPrintAtOffice} disabled={isSubmitting} className="submit-btn-office">
                         {isSubmitting ? 'Submitting...' : `Create & Print at Office`}
                    </button>
                </div>
                {isPrintModalOpen && createdDoc && (
                    <PrintToOfficeModal
                        isOpen={isPrintModalOpen}
                        docno={createdDoc.docno}
                        finyear={createdDoc.finyear}
                        doctype={docType}
                        headers={headers}
                        onClose={() => {
                        setIsPrintModalOpen(false);
                        setCreatedDoc(null);
                        toast.success(`${docType} created successfully!`);
                        navigate("/dashboard");
                        }}
                    />
                    )}
            </div>
        </>
    );
};

export default CreateInvoice;
